import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { GlobalFunctions, SnackType } from '@global/GlobalFunctions';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { distinctUntilChanged, filter, finalize, Subject, Subscription, takeUntil } from 'rxjs';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { IAccountInvite, IAccountInviteDetail, IAccountInviteResponse, InvitesService } from '@services/invites.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { IAccount } from '@services/accounts.service';
import { UserInvitationComponent } from 'src/app/dialogs/user-invitation/user-invitation.component';

/**
 * AccountInviteComponent
 */
@Component({
	selector: 'app-account-invite',
	templateUrl: './account-invite.component.html',
	styleUrls: ['./account-invite.component.scss']
})
export class AccountInviteComponent implements OnDestroy {

	/** Collection of the Subscription Objects for this Component */
	private subscriptions: Map<string, Subscription> = new Map();

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected User */
	public selectedUser: string

	/** Authenticated User */
	public thisUser: WebAppUser

	/** Account GUID */
	public accountGUID: string

	/** Invite GUID */
	public inviteGUID: string

	/** Invited Users List */
	public invitedUsers: IAccountInviteDetail[];
	public myInvites: IAccountInviteDetail[] = [];
	public oldInvites: IAccountInviteDetail[] = [];

	/** This Selected Invite */
	public selectedInvite: IAccountInviteDetail;
	/** Accout for invite */
	private InviteAccount: IAccount;

	/** show All Invitations */
	public showAllInvitations: boolean = false

	/** My Accounts */
	private myAccounts: IAccount[];

	/**
	 * Default Constructor
	 * @param auth Auth0 Authentication
	 * @param authenticationService Authentication Service
	 * @param formBuilder Form Builder
	 * @param globalFunctions Global Functions
	 * @param activatedRoute Activated Route
	 * @param inviteService: inviteService
	 * @param siteSubscriptions siteSubscriptions
	 * @param dialog dialog
	 */
	constructor(
		public auth: AuthService,
		public authenticationService: AuthenticationService,
		private globalFunctions: GlobalFunctions,
		private activatedRoute: ActivatedRoute,
		private inviteService: InvitesService,
		private siteSubscriptions: siteSubscriptions,
		public dialog: MatDialog

	) {
		this.authenticationService.currentUser$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter(currentUser => !!currentUser)
		).subscribe({
			next: (currentUser: WebAppUser) => {
				if (currentUser.Sub) {
					this.thisUser = currentUser;
					this.subscribeToParams()
				};
			}
		});

		this.siteSubscriptions.myAccounts$.pipe(
			takeUntil(this.finalise), distinctUntilChanged(),
			filter(myAccounts => !!myAccounts)
		).subscribe({
			next: (myAccounts) => {
				if (myAccounts) { this.myAccounts = myAccounts; }
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
			this.unsubscribeFromAll();
		}
		catch { }
	}

	/**
	 * Subscribe to Active Route Parameter Map
	 */
	private subscribeToParams() {

		/** get return url from route parameters or default to '/'  */
		this.activatedRoute.queryParams.pipe(
			takeUntil(this.finalise), distinctUntilChanged()
		).subscribe({
			next: (queryParams) => {
				this.inviteGUID = queryParams["invite"] || "";
				this.accountGUID = queryParams["AccountUID"] || "";
				this.getAccountInvites(this.accountGUID, this.inviteGUID)
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.activatedRoute.queryParams", false); return },
		});
	}

	/**
	 * Get list of invited Users
	 * @param accountGUID
	 */
	private getAccountInvites(accountGUID: string = "", inviteGUID: string = "") {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.invitedUsers = []
		let data = {
			"publicUID": this.thisUser.Sub,
			"accountGUID": accountGUID,
			"inviteGUID": inviteGUID,
			"inviteEmail": this.thisUser.Email,
			"ShowAll": true
		}
		this.inviteService.getInvitations$(data).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((invites) => !!invites),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (invites) => {
				this.invitedUsers = <IAccountInviteDetail[]>invites
				this.myInvites = <IAccountInviteDetail[]>this.invitedUsers.filter(
					({ InviteEmail, Accepted, Canceled }) => InviteEmail.toLowerCase() == this.thisUser.Email.toLowerCase() && Accepted == false && Canceled == false
				);
				this.filterInvites(false)
			},
			error: (error) => {
				if (error.status == 404) {
					console.log("No Invitations");
				}
				console.error("this.inviteService.getInvitations$:", error)
			},
			//complete: () => { console.log("this.inviteService.getInvitations$: Complete"); }
		});
	}

	filterInvites(showAll: boolean) {
		this.oldInvites = <IAccountInviteDetail[]>this.invitedUsers.filter(
			({ InviteEmail, Email, Accepted, Canceled }) => (InviteEmail.toLowerCase() == this.thisUser.Email.toLowerCase() ||
				Email.toLowerCase() == this.thisUser.Email.toLowerCase()) && (Canceled == showAll || Canceled == false)
		);
	}

	/**
	 * Accept the Invitation
	 * @param inviteID
	 * @returns
	 */
	acceptInvite(inviteID: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Find the Invite
		this.selectedInvite = <IAccountInviteDetail>this.invitedUsers.find(({ InviteGUID }) => InviteGUID.toUpperCase() === inviteID.toUpperCase())
		if (this.selectedInvite) {
			let response = <IAccountInviteResponse>{
				/** GUID for the new invitation */
				"InviteGUID": this.selectedInvite.InviteGUID,
				/** Name of Invited User */
				"InviteName": this.selectedInvite.InviteName,
				/** Email ofn invited User */
				"InviteEmail": this.selectedInvite.InviteEmail,
				/** Date Invitation Expires */
				"InviteExpires": this.selectedInvite.InviteExpires,
				/** Custom Message Sent with Invitation */
				"InviteMessage": this.selectedInvite.InviteMessage,
				/** Invite User as an admin */
				"InviteAsAdmin": this.selectedInvite.InviteAsAdmin,
				/** Account GUID */
				"AccountGUID": this.selectedInvite.AccountGUID,
				/** Name of the Account for Invitation */
				"AccountName": this.selectedInvite.AccountName,
				/** Response User ID */
				"ResponseUserID": this.thisUser.UserID,
				/** DisplayName of user sending the Invite */
				"Name": this.selectedInvite.Name,
				/** Email of user sending the Invite */
				"Email": this.selectedInvite.Email
			}

			this.inviteService.inviteAccept$(response.AccountGUID, response).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					// console.log(response)
					this.getAccountInvites()
					this.refreshAccountsList()
					if (response['Error']) {
						// this.globalFunctions.displaySpinner(false)
						this.globalFunctions.DisplaySnackBar({
							message: response['Error'],
							duration: 2000,
							type: SnackType.Error,
							action: ""
						})
					} else {// this.globalFunctions.displaySpinner(false)
						this.globalFunctions.DisplaySnackBar({
							message: "Invitation to " + this.selectedInvite.AccountName + " has been accepted",
							duration: 2000,
							action: ""
						})
					}
				},
				error: (error) => {
					this.globalFunctions.displaySpinner(false)
					if (error.status = 404) {
						this.globalFunctions.DisplaySnackBar({
							message: error.error,
							duration: 2000,
							action: "",
							type: SnackType.Error
						})

						this.getAccountInvites()
						this.refreshAccountsList()
						return
					}
					this.globalFunctions.HandleError(error, "cancelInvite: Error accepting Invite")
				},
				complete: () => {
					this.globalFunctions.displaySpinner(false)
					this.refreshAccountsList()
				}
			});
		}
	}

	/** Refresh My Accounts Subscription */
	refreshAccountsList() {
		let data = {
			"userPublicID": this.thisUser.Sub,
			"showDeleted": this.thisUser.AdminSite
		}
		this.siteSubscriptions.RefreshMyAccounts(data)
	}

	/**
	 * Cancel an invitation
	 * @param inviteID
	 * @returns
	 */
	cancelInvite(inviteID: any) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Find the Invite
		this.selectedInvite = <IAccountInviteDetail>this.invitedUsers.find(({ InviteGUID }) => InviteGUID.toUpperCase() === inviteID.toUpperCase())

		if (this.selectedInvite) {

			const dialogRef = this.dialog.open(CommentDialogComponent, {
				width: '500px',
				data: {
					"HeaderText": "Cancel Invitation",
					"DialogText": "Cancel the Invitation from " + this.selectedInvite.Name + "?"
				}
			});
			dialogRef.afterClosed().pipe(
				filter(result => !!result)
			).subscribe(result => {
				// Perform the delete action with the comment
				// console.log('The dialog was closed with comment:', result.comment);
				// Add your delete logic here
				this.selectedInvite.CancelReason = result.comment;

				this.globalFunctions.displaySpinner(true)
				this.inviteService.inviteCancel$(this.selectedInvite.InviteGUID, this.selectedInvite).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (response) => {
						// console.log(response)
						this.getAccountInvites()

						if (response['Error']) {
							// this.globalFunctions.displaySpinner(false)
							this.globalFunctions.DisplaySnackBar({
								message: response['Error'],
								duration: 2000,
								type: SnackType.Error,
								action: ""
							})
						} else {// this.globalFunctions.displaySpinner(false)
							this.globalFunctions.DisplaySnackBar({
								message: response['Result'],
								duration: 2000,
								action: ""
							})
						}
					},
					error: (error) => {
						this.globalFunctions.displaySpinner(false)
						this.globalFunctions.HandleError(error, "cancelInvite: Error cancelling Invite")
					},
					complete: () => {
						this.globalFunctions.displaySpinner(false)
					}
				});
			});
		}
	}

	/**
	 * Resend the Invite
	 * @param account
	 */
	resendInvite(inviteID: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Find the Invite
		this.selectedInvite = <IAccountInviteDetail>this.invitedUsers.find(({ InviteGUID }) => InviteGUID.toUpperCase() === inviteID.toUpperCase())
		this.InviteAccount = <IAccount>this.myAccounts.find(({ ID }) => ID == this.selectedInvite.AccountID);

		/** You need to ba an admin */
		if (!this.InviteAccount.Admin) { return }

		// Create a new Account Invitation
		var _edit = <IAccountInvite>{
			"InviteName": this.selectedInvite.InviteName || "",
			"InviteEmail": this.selectedInvite.InviteEmail || "",
			"InviteMessage": this.selectedInvite.InviteMessage || "",
			"InviteAsAdmin": this.selectedInvite.InviteAsAdmin || false,
			"Resend": true
		}

		// Display the dialog box
		const dialogRef = this.dialog.open(UserInvitationComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);

			let serverAddress = window.location.protocol + "//" + window.location.host
			// serverAddress = 'https://prismpdm.io/'

			//Update the Account GID
			_edit.AccountUID = this.InviteAccount.GUID
			_edit.InviteUserID = this.thisUser.Sub
			_edit.InviteServerURL = serverAddress

			this.inviteService.inviteAccountUser$(this.accountGUID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					if (response['Error'] != "") {

					} else {
						// this.globalFunctions.displaySpinner(false)
						this.globalFunctions.DisplaySnackBar({
							message: response['Result'],
							duration: 2000,
							action: "",
							type: SnackType.Info
						})
						this.getAccountInvites(this.accountGUID)
					}

				},
				error: (error) => {
					this.globalFunctions.displaySpinner(false)
					if (error.status = 404) {
						this.globalFunctions.DisplaySnackBar({
							message: error.error,
							duration: 2000,
							action: "",
							type: SnackType.Error
						})
						return
					}
					this.globalFunctions.HandleError(error, "sendInvite: Error Sending Invite")
				},
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
		});
	}

	/**
	 * Method to unsubscribe from a specific observable by key
	 */
	private unsubscribeFromObservable(key: string) {
		try {
			const subscription = this.subscriptions.get(key);
			if (subscription) {
				subscription.unsubscribe();
				this.subscriptions.delete(key);
			}
		} catch (error) { }
	}

	/**
	 * Method to unsubscribe from all subscriptions
	 */
	private unsubscribeFromAll() {
		try {
			this.subscriptions.forEach(subscription => subscription.unsubscribe());
			this.subscriptions.clear();
		} catch (error) { }
	}
}
